<script>
    // Licensed under Gnu Public License version 2
    // Copyright (c) 2024 (original work) Open Assessment Technologies SA ;

    import { createEventDispatcher } from 'svelte';
    import { IconBarButton } from '@oat-sa-private/ui-elements';
    import FlyoutMenu from '../flyoutMenu/FlyoutMenu.svelte';
    import ExpandButton from './ExpandButton.svelte';

    export let data;
    export let actionable;
    export let actionableLabel;
    export let actionableMenuItems;
    export let nameProperty;
    export let level = 0;

    //in some cases level is needed to define menu entries
    if (data) {
        data.level = level;
    }

    let showFlyout = false;
    let menuItems = [];
    let shouldRenderFlyoutMenu = false;

    const dispatch = createEventDispatcher();

    $: if (actionable) {
        if (Array.isArray(actionableMenuItems) && actionableMenuItems.length > 0) {
            menuItems = actionableMenuItems;
            shouldRenderFlyoutMenu = true;
        } else if (typeof actionableMenuItems === 'function') {
            menuItems = actionableMenuItems(data);
            if (menuItems.length) {
                shouldRenderFlyoutMenu = true;
            }
        } else {
            shouldRenderFlyoutMenu = false;
        }
    }

    let nodeElement;

    function toggleNode(eventData) {
        dispatch('toggle', eventData);
    }

    function selectNode(eventData) {
        dispatch('select', eventData);
    }

    function handleMenuAction(e) {
        dispatch('menuAction', { action: e.detail.key, data });
    }

    function toggleActionMenu() {
        showFlyout = !showFlyout;
    }

    function closeActionMenu() {
        showFlyout = false;
    }

    function handleSelectionClick(e) {
        e.preventDefault();
        selectNode(data);
        closeActionMenu();
    }

    function handleToggleClick(e) {
        e.preventDefault();
        toggleNode(data);
        closeActionMenu();
    }

    function handleActionButtonClick(e) {
        e.preventDefault();
        toggleActionMenu();
    }

    function decorateSearch(search, text) {
        //sanitize any html content
        const doc = new DOMParser().parseFromString(text, 'text/html');
        text = doc.body.textContent || '';

        //add bold highlights
        if (search) {
            const re = new RegExp(search, 'ig');
            return text.replaceAll(re, match => `<b>${match}</b>`);
        } else {
            return text;
        }
    }
</script>

<style>
    @define-mixin positioned-above {
        position: relative;
        z-index: var(--layer-1);
    }

    @define-mixin remove-button-style {
        background: none;
        outline: none;
        border: 0;
    }

    li.node {
        --local_action-button-container-margin-left: var(--action-button-container-margin-left, var(--space-2x));

        /* base-16 as default size */
        --local_action-button-placeholder-size: var(--action-button-placeholder-size, 5.5rem);
        --local_action-button-placeholder-width: var(
            --action-button-placeholder-width,
            var(--local_action-button-placeholder-size)
        );
        --local_action-button-placeholder-height: var(
            --action-button-placeholder-height,
            var(--local_action-button-placeholder-size)
        );

        display: flex;
        flex: 5.5rem 0 0;
        align-items: stretch;
        justify-content: flex-start;

        & .selected-indicator {
            --local_level: var(--level, 0);

            @add-mixin positioned-above;

            flex: 0.5rem 0 0;
            margin-right: calc(3rem * var(--local_level));
        }

        & .name-container {
            @add-mixin positioned-above;
            @add-mixin remove-button-style;

            line-height: 5.5rem;
            white-space: nowrap;
            text-overflow: ellipsis;
            text-align: left;
            font-size: var(--fontsize-body);
            display: flex;
            flex-grow: 1;

            & span {
                flex-grow: 1;
            }

            &.action-placeholder:after {
                content: '';

                @add-mixin positioned-above;

                flex: 5rem 0 0;

                margin-left: var(--local_action-button-container-margin-left);
                width: var(--local_action-button-placeholder-width);
                height: var(--local_action-button-placeholder-height);
            }
        }

        & .action-button-container {
            @add-mixin positioned-above;

            flex: 5rem 0 0;
            margin-left: var(--local_action-button-container-margin-left);
        }

        & .node-container {
            @add-mixin remove-button-style;

            display: flex;
            position: relative;
            align-items: stretch;
            justify-content: flex-start;
            padding: 0;
            width: 100%;

            &.selected {
                background-color: var(--color-node-selected);

                & .selected-indicator {
                    background-color: var(--color-node-selected-indicator);
                }

                & .name-container {
                    /* to avoid layout swift */
                    transition: text-shadow 0.1s;
                    text-shadow: 0 0 0.65px currentColor, 0 0 0.65px currentColor;
                }
            }

            &:hover {
                background-color: var(--color-node-hover);
            }
        }

        & .focus-hook {
            @add-mixin remove-button-style;
            @add-mixin outline-focus 0;

            position: absolute;
            z-index: var(--layer-0);
            width: 100%;
            height: 100%;

            &:focus-visible {
                background-color: var(--color-node-hover);
            }
        }
    }

    :global(li.node .action-button-container.active button) {
        border: var(--border-thin) solid var(--color-border-default);
    }

    :global(li.node .expand-button-container) {
        @add-mixin positioned-above;
    }
</style>

{#if data && !data.hidden}
    <li class="node">
        <div class="node-container" class:selected={data.selected}>
            <button class="focus-hook" on:click={handleSelectionClick} aria-label={data[nameProperty]} />
            <div class="selected-indicator" style={`--level: ${level};`} />
            <ExpandButton
                expanded={data.expanded}
                expandable={data.children && data.children.length}
                on:click={handleToggleClick} />
            <button
                class="name-container"
                class:action-placeholder={shouldRenderFlyoutMenu && !data.selected}
                tabindex="-1"
                aria-hidden="true"
                on:click={handleSelectionClick}>
                <span>{@html decorateSearch(data.search, data[nameProperty])}</span>
            </button>
            {#if data.selected && shouldRenderFlyoutMenu}
                <div class="action-button-container" class:active={showFlyout} bind:this={nodeElement}>
                    <IconBarButton
                        icon="more-16"
                        size="base-16"
                        label={actionableLabel}
                        shape="circular"
                        on:click={handleActionButtonClick} />
                </div>
            {/if}
        </div>
        {#if data.selected && shouldRenderFlyoutMenu}
            <FlyoutMenu
                bind:open={showFlyout}
                items={menuItems}
                caller={nodeElement}
                on:action={handleMenuAction}
                anchoring="top right" />
        {/if}
    </li>
    {#if data.children && data.expanded}
        {#each data.children as child}
            <svelte:self
                data={child}
                level={level + 1}
                {nameProperty}
                {actionable}
                {actionableLabel}
                {actionableMenuItems}
                on:toggle
                on:select
                on:menuAction />
        {/each}
    {/if}
{/if}
